// ============================================
// CONFIRM DIALOG COMPONENT
// ============================================

import { ReactNode, useState, useEffect } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '@/utils';

interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string | ReactNode;
  confirmText?: string;
  cancelText?: string;
  onConfirm: (reason?: string) => void;
  onCancel?: () => void;
  variant?: 'default' | 'danger' | 'warning' | 'success';
  loading?: boolean;
  showReasonInput?: boolean;
  reasonPlaceholder?: string;
  reasonRequired?: boolean;
}

const variants = {
  default: {
    icon: Info,
    iconColor: 'text-maritime-600 bg-maritime-100',
    buttonColor: 'bg-maritime-600 hover:bg-maritime-700 text-white',
  },
  danger: {
    icon: XCircle,
    iconColor: 'text-red-600 bg-red-100',
    buttonColor: 'bg-red-600 hover:bg-red-700 text-white',
  },
  warning: {
    icon: AlertTriangle,
    iconColor: 'text-amber-600 bg-amber-100',
    buttonColor: 'bg-amber-600 hover:bg-amber-700 text-white',
  },
  success: {
    icon: CheckCircle2,
    iconColor: 'text-green-600 bg-green-100',
    buttonColor: 'bg-green-600 hover:bg-green-700 text-white',
  },
};

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Onayla',
  cancelText = 'İptal',
  onConfirm,
  onCancel,
  variant = 'default',
  loading = false,
  showReasonInput = false,
  reasonPlaceholder = 'Sebep belirtin (opsiyonel)',
  reasonRequired = false,
}: ConfirmDialogProps) {
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) {
      setReason('');
    }
  }, [open]);

  if (!open) return null;

  const config = variants[variant];
  const Icon = config.icon;

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const handleConfirm = () => {
    if (reasonRequired && showReasonInput && !reason.trim()) {
      return;
    }
    onConfirm(reason || undefined);
    if (!loading) {
      onOpenChange(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleCancel}
      />

      {/* Dialog */}
      <div className="relative w-full max-w-md bg-white rounded-maritime shadow-maritime-lg p-6 animate-fade-in">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={cn('flex-shrink-0 p-2 rounded-full', config.iconColor)}>
            <Icon className="h-6 w-6" />
          </div>

          {/* Content */}
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
            {description && (
              <div className="mt-2 text-sm text-gray-500">{description}</div>
            )}
          </div>
        </div>

        {/* Reason Input */}
        {showReasonInput && (
          <div className="mt-4">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={reasonPlaceholder}
              rows={3}
              className={cn(
                'w-full px-3 py-2 text-sm border rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500',
                reasonRequired && !reason.trim() ? 'border-red-300' : 'border-gray-300'
              )}
            />
            {reasonRequired && !reason.trim() && (
              <p className="mt-1 text-xs text-red-500">Sebep belirtmek zorunludur</p>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="mt-6 flex justify-end gap-3">
          <button
            onClick={handleCancel}
            disabled={loading}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-maritime transition-colors disabled:opacity-50"
          >
            {cancelText}
          </button>
          <button
            onClick={handleConfirm}
            disabled={loading || (reasonRequired && showReasonInput && !reason.trim())}
            className={cn(
              'px-4 py-2 text-sm font-medium rounded-maritime transition-colors disabled:opacity-50',
              config.buttonColor
            )}
          >
            {loading ? 'İşleniyor...' : confirmText}
          </button>
        </div>
      </div>
    </div>
  );
}
